import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import TeamStatusAccordionView from '../components/team-availability/TeamStatusAccordionView';
import { UserRole } from '../components/team-members';

const VIEW_OPTIONS = [
  { value: 'list', label: 'List' },
  { value: 'calendar', label: 'Calendar' },
  { value: 'pto', label: 'PTO' },
];

export default function TeamAvailability() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeView, setActiveView] = React.useState('list');
  const { user: currentUser, loading: authLoading } = useAuth();
  const canManageAvailability = currentUser?.role === UserRole.ADMIN;
  const linkedUserId = Number(searchParams.get('userId')) || null;
  const linkedRequestId = searchParams.get('requestId') || null;
  
  // The dashboard route handles the role privilege; this page only needs to
  // preserve the existing signed-in redirect while auth initializes.
  React.useEffect(() => {
    if (!authLoading && !currentUser) {
      navigate('/login');
    }
  }, [currentUser, authLoading, navigate]);

  if (authLoading) {
    return (
      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center items-center min-h-[200px]">
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
      <div className="sm:px-0">
        {/* Header */}
        <div className="mb-4 flex flex-col gap-4 sm:mb-6 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:grid-rows-[auto_auto] lg:items-center lg:gap-x-6 lg:gap-y-1.5">
          <h1 className="text-2xl font-semibold text-gray-100 lg:col-start-1 lg:row-start-2 lg:self-center lg:text-4xl lg:leading-none lg:text-gray-800">
            Team Availability
          </h1>

          <div className="lg:contents">
            <div className="mb-1.5 text-center text-sm font-semibold uppercase tracking-wider text-gray-600 lg:col-start-2 lg:row-start-1 lg:mb-0 lg:self-end">View</div>
            <div className="inline-flex rounded-xl border-2 border-sky-200 bg-white p-1.5 shadow-md lg:col-start-2 lg:row-start-2 lg:justify-self-center" role="group" aria-label="Team availability view">
              {VIEW_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setActiveView(option.value)}
                  aria-pressed={activeView === option.value}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors sm:px-5 ${
                    activeView === option.value
                      ? 'bg-sky-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-sky-50 hover:text-sky-800'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {canManageAvailability && (
            <div className="flex self-start items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm lg:col-start-3 lg:row-start-2 lg:self-center lg:justify-self-end">
              <div className="max-w-[190px] text-xs leading-5 text-gray-600">
                Adjust PTO days and company holidays.
              </div>
              <button
                onClick={() => navigate('/dashboard/team-availability/settings')}
                className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300"
              >
                Settings
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <TeamStatusAccordionView
          viewMode={activeView}
          canManageAvailability={canManageAvailability}
          initialUserId={linkedUserId}
          initialRequestId={linkedRequestId}
        />
      </div>
    </div>
  );
}
